import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import advertiserImg from '@/assets/apex-advertiser.jpg'

// Animated JSON payload for the auth art panel. Shows what Upspring hands an AI:
// an advertiser / creative / query resolving into reasoning-ready JSON.
// `variant="auto"` (default) rotates through all three, a few seconds each,
// crossfading; a specific variant name pins one. `theme` ('dark' | 'light')
// flips the text colors so the payload reads on a dark or light panel.
//
// Line markup: `text` = string value (ink), ~text~ = accent value
// (grades, estimates), everything else = muted keys/punctuation.

const SEQUENCE = ['enriched', 'query', 'creative']
const PERIOD_MS = 6500

// The real Apex Athletics ad creative, same public GCS asset the landing page
// uses. Serves as both the still (first frame) and the playing video.
const CREATIVE_VIDEO_URL = 'https://storage.googleapis.com/spring-assets-prod/Apex/video_5.mp4'

const THEME = {
    dark: {
        accent: 'text-auth-accent-soft',
        ink: 'text-auth-machine-ink',
        mute: 'text-auth-machine-mute',
        border: 'border-auth-machine-rule',
        dot: 'bg-auth-machine-rule',
        box: 'border-auth-machine-rule bg-black/25',
    },
    light: {
        accent: 'text-auth-accent',
        ink: 'text-auth-ink',
        mute: 'text-auth-muted',
        border: 'border-auth-rule',
        dot: 'bg-auth-rule',
        box: 'border-auth-rule bg-black/[0.03]',
    },
}

const renderLine = (str, t) => {
    const parts = str.split(/(~[^~]*~|`[^`]*`)/g).filter(Boolean)
    return parts.map((p, i) => {
        if (p.startsWith('~')) {
            return <span key={i} className={t.accent}>{p.slice(1, -1)}</span>
        }
        if (p.startsWith('`')) {
            return <span key={i} className={t.ink}>{p.slice(1, -1)}</span>
        }
        return <span key={i} className={t.mute}>{p}</span>
    })
}

const ENRICHED = [
    '{',
    '  advertiser: `Apex Athletics`,',
    '  domain: `apexathletics.com`,',
    '  vertical: `activewear`,',
    '  category: `Leggings & tights`,',
    '  scale: { spend: ~A-~ traffic: ~A~ sales: ~B+~ },',
    '  enrich: {',
    '    spend_est:   ~$200-400K /mo~,',
    '    sales_est:   ~$2.1-3.6M /mo~,',
    '    traffic_est: ~710K /mo~,',
    '  },',
    '  sentiment: { positive: ~0.82~ },',
    '  personas: [ `Active couples`, +2 ],',
    '  media_mix: { video: ~68%~, image: ~32%~ },',
    '  social: { ig: ~640K~, fb: ~210K~ },',
    '  active_ads: ~128~,',
    '}',
]

const CREATIVE = [
    'INPUT',
    '{',
    '  media: { kind: `video`, ratio: `9:16`, len: ~8~ },',
    '  advertiser: `Apex Athletics`,',
    '  hook_text: `Unleash your movement…`,',
    '  dimensions: {',
    '    hook_archetype: `Lifestyle aspiration`,',
    '    persona: `Active couples`,',
    '    + 6 more',
    '  },',
    '  scenes: [',
    '    `Couple runs a coastal road at sunset`,',
    '    `Product close-up of camo leggings`,',
    '    + 3 more',
    '  ],',
    '  product: { title: `Apex Camo Legging`, price: ~$68~ },',
    '  offer: { cta: `Shop now` },',
    '  landing_page: { coherence: ~0.82~ },',
    '  strength: ~4/5 · high~,',
    '}',
]

const QUERIES = [
    {
        endpoint: 'POST /v1/query',
        q: 'what angles am I overusing vs rivals?',
        lines: [
            '{',
            '  vertical: `Skincare`,',
            '  window: `last 90 days`,',
            '  ranked_by: `angle · rivals vs you`,',
            '  results: [',
            '    { angle: `hormonal aging`,  rivals: ~A+~, you: ~D~ },',
            '    { angle: `menopausal skin`, rivals: ~A~,  you: ~D~ },',
            '    { angle: `peptide repair`,  rivals: ~B+~, you: ~C~ },',
            '  ],',
            '  whitespace: [ `barrier repair`, `fragrance-free` ],',
            '}',
        ],
    },
    {
        endpoint: 'POST /v1/query',
        q: 'who is scaling fastest in my category?',
        lines: [
            '{',
            '  category: `Leggings & tights`,',
            '  sorted_by: `spend_velocity`,',
            '  results: [',
            '    { brand: `Apex Athletics`, scale: ~A-~, trend: ~+38%~ },',
            '    { brand: `Gruns`,          scale: ~B+~, trend: ~+21%~ },',
            '    { brand: `Ridge`,          scale: ~B~,  trend: ~+12%~ },',
            '  ],',
            '  new_entrants: ~4~,',
            '}',
        ],
    },
    {
        endpoint: 'POST /v1/advertisers/peers',
        q: 'find advertisers like this one',
        lines: [
            '{',
            '  seed: `apexathletics.com`,',
            '  matched_on: `audience + positioning`,',
            '  peers: [',
            '    { brand: `Vuori`,      similarity: ~0.91~ },',
            '    { brand: `Alo`,        similarity: ~0.88~ },',
            '    { brand: `Set Active`, similarity: ~0.84~ },',
            '  ],',
            '  shared_personas: [ `Active couples`, `Studio regulars` ],',
            '}',
        ],
    },
]

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const Lines = ({ lines, reduced, t }) => (
    <div className="whitespace-pre font-mono text-[14px] leading-[1.8]">
        {lines.map((ln, i) => (
            <motion.div
                key={i}
                initial={reduced ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: reduced ? 0 : i * 0.09 }}
            >
                {renderLine(ln, t)}
            </motion.div>
        ))}
    </div>
)

const mediaClass = (t) => `aspect-[9/16] w-[220px] shrink-0 self-start rounded-[6px] border ${t.border} object-cover`

const label = (t) => `mb-3 font-mono text-[11px] uppercase tracking-[0.14em] ${t.mute}`

// The advertiser's still creative (a frame of the Apex ad).
const AdImage = ({ t }) => (
    <img src={advertiserImg} alt="" className={mediaClass(t)} />
)

// The live Apex ad, only in the creative card. Autoplays muted (first frame
// only under reduced motion).
const AdVideo = ({ reduced, t }) => (
    <video
        src={CREATIVE_VIDEO_URL}
        autoPlay={!reduced}
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
            try {
                e.currentTarget.currentTime = 1.5
            } catch {
                /* seeking not ready yet — fine */
            }
        }}
        className={mediaClass(t)}
    />
)

const useTick = (ms, enabled) => {
    const [t, setT] = useState(0)
    useEffect(() => {
        if (!enabled) {
            return
        }
        const id = setInterval(() => setT((x) => x + 1), ms)
        return () => clearInterval(id)
    }, [ms, enabled])
    return t
}

export function AuthPayload({ variant = 'auto', className, theme = 'dark' }) {

    const t = THEME[theme] || THEME.dark
    const reduced = prefersReducedMotion()
    const rotating = variant === 'auto'
    // Reduced-motion users get one static payload (no rotation, no stagger).
    const step = useTick(PERIOD_MS, !reduced)
    const active = rotating ? SEQUENCE[step % SEQUENCE.length] : variant

    const renderActive = () => {
        if (active === 'query') {
            const qIndex = rotating
                ? Math.floor(step / SEQUENCE.length) % QUERIES.length
                : step % QUERIES.length
            const current = QUERIES[qIndex]
            return (
                <div className={`max-w-[580px] rounded-[6px] border p-6 ${t.box}`}>
                    <div className="mb-4 flex items-center gap-1.5">
                        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                        <span className={`ml-2 font-mono text-[11px] uppercase tracking-[0.14em] ${t.mute}`}>upspring · api</span>
                    </div>
                    <div className={`mb-3 flex items-start gap-2 font-mono text-[14px] ${t.ink}`}>
                        <span className={t.accent}>›_</span>
                        <span>{current.q}</span>
                    </div>
                    <div className={`mb-2 font-mono text-[13px] ${t.mute}`}>
                        {current.endpoint}{'  '}<span className={t.accent}>200 OK</span>
                    </div>
                    <Lines lines={current.lines} reduced={reduced} t={t} />
                </div>
            )
        }
        if (active === 'creative') {
            return (
                <div className="flex flex-col gap-5 xl:flex-row">
                    <AdVideo reduced={reduced} t={t} />
                    <div>
                        <div className={label(t)}>Creative → JSON</div>
                        <Lines lines={CREATIVE} reduced={reduced} t={t} />
                    </div>
                </div>
            )
        }
        return (
            <div className="flex flex-col gap-5 xl:flex-row">
                <AdImage t={t} />
                <div>
                    <div className={label(t)}>Advertiser → JSON</div>
                    <Lines lines={ENRICHED} reduced={reduced} t={t} />
                </div>
            </div>
        )
    }

    return (
        <div className={`min-h-[520px] ${className || ''}`}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={rotating ? `${active}-${step}` : active}
                    initial={reduced ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduced ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {renderActive()}
                </motion.div>
            </AnimatePresence>
        </div>
    )
}
